import { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toggleToTechnicalUser, toggleToPatientUser, toggleToOphthalmologistUser, toggleToNewStatus, toggleToContinueStatus, toggleToLockedStatus, toggleToUnlockStatus, toggleToAdminUser } from '../../redux/user'
import { setEvaluations as setCurrentEvaluation } from '../../redux/evaluations'
import { USER_MANUAL_LABEL, USER_STATUS } from '../../const'
import { useAdminAPI } from '../../hooks/Admin/useAdminAPI'
import { useCompleteEvaluationData } from '../../hooks/useCompleteEvaluationData'
import Hamburger from '../../assets/icons/hamburger.png'
import RetinarLogo from '../../assets/images/lightblue_logo_small.svg'
import { openUserManual } from '../../services/openUserManual'
import AboutRetinar from '../../views/AboutRetinar'
import { ABOUT_RETINAR_CONTENT } from '../../views/AboutRetinar/data'
import {
  Container,
  MenuSide,
  AvatarSide,
  MenuIcon,
  Logo,
  AvatarLetter,
  HamburgerMenu,
  HamburgerOption,
  UserMenu,
  RoleWrapper,
  StatusWrapper,
  Label,
  Input
} from './styles'

export function Header () {
  const { user } = useSelector(state => state)
  const dispatch = useDispatch()
  const hamburguerMenuRef = useRef(null)
  const userRef = useRef(null)
  const [showMenu, setShowMenu] = useState(false)
  const [showUser] = useState(false)
  const [showAboutRetinar, setShowAboutRetinar] = useState(false)
  const [pendingStudiesCount, setPendingStudiesCount] = useState(0)
  const [pendingStudiesLoading, setPendingStudiesLoading] = useState(false)
  const [continueStudyLoading, setContinueStudyLoading] = useState(false)
  const { fetchEntities } = useAdminAPI()
  const { completeEvaluationData } = useCompleteEvaluationData()
  const history = useHistory()

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (user.role !== 'ophthalmologist' || user.status === USER_STATUS.CONTINUE) {
      setPendingStudiesCount(0)
      setPendingStudiesLoading(false)
      return
    }

    let active = true

    const loadPendingStudiesCount = async () => {
      try {
        setPendingStudiesLoading(true)
        const studies = await fetchEntities('estudios/pendientes')
        if (!active) return
        setPendingStudiesCount(Array.isArray(studies) ? studies.length : 0)
      } catch (error) {
        if (!active) return
        setPendingStudiesCount(0)
      } finally {
        if (active) {
          setPendingStudiesLoading(false)
        }
      }
    }

    loadPendingStudiesCount()

    return () => {
      active = false
    }
  }, [fetchEntities, user.role, user.status])

  const handleClickOutside = (event) => {
    if (hamburguerMenuRef && !hamburguerMenuRef.current.contains(event.target)) {
      closeMenu()
    }
  }

  const toggleShowMenu = () => {
    setShowMenu(prevState => !prevState)
  }

  const closeMenu = () => {
    setShowMenu(false)
  }

  const handleGoHome = () => {
    closeMenu()
    history.push('/home')
  }

  const handleGoEvaluations = () => {
    closeMenu()
    history.push('/evaluations')
  }

  const handleGoNewPatient = () => {
    closeMenu()
    history.push('/new-patient')
  }

  const handleGoAdminInstitutions = () => {
    closeMenu()
    history.push('/admin/institutions')
  }

  const handleGoAdminTechnicians = () => {
    closeMenu()
    history.push('/admin/technicians')
  }

  const handleGoAdminOphthalmologists = () => {
    closeMenu()
    history.push('/admin/ophthalmologists')
  }

  const handleGoAdminAdmins = () => {
    closeMenu()
    history.push('/admin/admins')
  }

  const handleGoAdminInstitutionAdmins = () => {
    closeMenu()
    history.push('/admin/institution-admins')
  }

  const handleGoNewCase = () => {
    closeMenu()
    history.push('/new-case')
  }

  const handleGoPendingReports = () => {
    closeMenu()
    history.push('/pending-reports')
  }

  const handleOpenUserManual = () => {
    closeMenu()
    openUserManual()
  }

  const handleOpenAboutRetinar = () => {
    closeMenu()
    setShowAboutRetinar(true)
  }

  const handleCloseAboutRetinar = () => {
    setShowAboutRetinar(false)
  }

  const handleGoContinueStudy = async () => {
    if (continueStudyLoading) return

    closeMenu()
    setContinueStudyLoading(true)

    try {
      const study = await fetchEntities('estudios/pendiente')
      if (!study || study.mensaje) return
      dispatch(setCurrentEvaluation(study))
      dispatch(toggleToContinueStatus())
      await completeEvaluationData(study, 'pending')
      history.push('/new-report')
    } catch (error) {
      console.error('No fue posible continuar el estudio en curso', error)
    } finally {
      setContinueStudyLoading(false)
    }
  }

  const handleLogOut = () => {
    window.sessionStorage.clear()
    closeMenu()
    history.push('/login')
  }

  const handleChangeRole = (event) => {
    const selectedRole = event.target.value

    if (selectedRole === 'technical') {
      dispatch(toggleToTechnicalUser())
      const userToStore = { role: 'technical', status: null }
      window.sessionStorage.setItem('_user', JSON.stringify(userToStore))
    }
    if (selectedRole === 'ophthalmologist') {
      dispatch(toggleToOphthalmologistUser())
      const userToStore = { role: 'ophthalmologist', status: 'new' }
      window.sessionStorage.setItem('_user', JSON.stringify(userToStore))
    }
    if (selectedRole === 'patient') {
      dispatch(toggleToPatientUser())
      const userToStore = { role: 'patient', status: null }
      window.sessionStorage.setItem('_user', JSON.stringify(userToStore))
    }
    if (selectedRole === 'admin') {
      dispatch(toggleToAdminUser())
      const userToStore = { role: 'admin', status: null }
      window.sessionStorage.setItem('_user', JSON.stringify(userToStore))
    }
    history.push('/home')
  }

  const handleChangeOphthalmologistStatus = (event) => {
    const { value } = event.target
    const DISPATCH_OPTIONS = {
      new: toggleToNewStatus,
      continue: toggleToContinueStatus,
      locked: toggleToLockedStatus,
      unlock: toggleToUnlockStatus
    }

    const userAction = DISPATCH_OPTIONS[value]
    dispatch(userAction())
    const userToStore = { role: 'ophthalmologist', status: value }
    window.sessionStorage.setItem('_user', JSON.stringify(userToStore))
    history.push('/home')
  }

  const isAdmin = user.role === 'admin'
  const isTechnical = user.role === 'technical'
  const isOphthalmologist = user.role === 'ophthalmologist'
  const hasPendingStudies = pendingStudiesCount > 0

  const ophthalmologistCaseOptionLabel = user.status === USER_STATUS.CONTINUE
    ? (continueStudyLoading ? 'Cargando...' : 'Continuar estudio')
    : (
        pendingStudiesLoading
          ? 'Cargando...'
          : (
              hasPendingStudies
                ? 'Informar estudios'
                : 'No hay estudios para informar'
            )
      )

  const isOphthalmologistCaseOptionDisabled = user.status === USER_STATUS.CONTINUE
    ? continueStudyLoading
    : (pendingStudiesLoading || !hasPendingStudies)

  const handleOphthalmologistCaseOption = () => {
    if (user.status === USER_STATUS.CONTINUE) {
      handleGoContinueStudy()
      return
    }

    if (hasPendingStudies) {
      handleGoPendingReports()
    }
  }

  const renderGlobalMenuOptions = () => (
    <>
      <HamburgerOption onClick={handleOpenAboutRetinar}>{ABOUT_RETINAR_CONTENT.menuLabel}</HamburgerOption>
      <HamburgerOption onClick={handleOpenUserManual}>{USER_MANUAL_LABEL}</HamburgerOption>
    </>
  )

  const renderMenuOptions = () => {
    if (isAdmin) {
      return (
        <>
          <HamburgerOption onClick={handleGoAdminInstitutions}> Administrar instituciones </HamburgerOption>
          <HamburgerOption onClick={handleGoAdminTechnicians}> Administrar técnicos </HamburgerOption>
          <HamburgerOption onClick={handleGoAdminOphthalmologists}> Administrar oftalmólogos </HamburgerOption>
          <HamburgerOption onClick={handleGoAdminAdmins}> Administrar administradores </HamburgerOption>
          <HamburgerOption onClick={handleGoAdminInstitutionAdmins}> Administrar admins. de institución </HamburgerOption>
          {renderGlobalMenuOptions()}
        </>
      )
    }

    if (isTechnical) {
      return (
        <>
          <HamburgerOption onClick={handleGoEvaluations}> Estudios del nodo </HamburgerOption>
          <HamburgerOption onClick={handleGoNewPatient}> Alta de paciente </HamburgerOption>
          <HamburgerOption onClick={handleGoNewCase}> Nuevo estudio </HamburgerOption>
          {renderGlobalMenuOptions()}
        </>
      )
    }

    if (isOphthalmologist) {
      return (
        <>
          <HamburgerOption onClick={handleGoEvaluations}> Mis estudios informados </HamburgerOption>
          <HamburgerOption
            onClick={handleOphthalmologistCaseOption}
            $disabled={isOphthalmologistCaseOptionDisabled}
          >
            {ophthalmologistCaseOptionLabel}
          </HamburgerOption>
          {renderGlobalMenuOptions()}
        </>
      )
    }

    return (
      <>
        <HamburgerOption onClick={handleGoHome}> Inicio </HamburgerOption>
        <HamburgerOption onClick={handleGoEvaluations}> Listado de evaluaciones </HamburgerOption>
        {/* <HamburgerOption onClick={handleGoNewCase}> Nuevo estudio </HamburgerOption> */}
        {renderGlobalMenuOptions()}
      </>
    )
  }

  return (
    <Container>
      <MenuSide>
        <HamburgerMenu ref={hamburguerMenuRef} show={showMenu}>
          {renderMenuOptions()}
          <HamburgerOption onClick={handleLogOut}> Cerrar sesión </HamburgerOption>
        </HamburgerMenu>

        <MenuIcon src={Hamburger} alt='menu' onClick={toggleShowMenu} />
        <Logo src={RetinarLogo} onClick={handleGoHome} alt='logo' />
      </MenuSide>

      <AboutRetinar isOpen={showAboutRetinar} onClose={handleCloseAboutRetinar} />

      <AvatarSide>
        <UserMenu ref={userRef} show={showUser}>
          <RoleWrapper>
            <Label>
              Técnico
              <Input name='role' onChange={handleChangeRole} checked={user.role === 'technical'} type='radio' value='technical' />
            </Label>
            <Label>
              Oftalmólogo
              <Input name='role' onChange={handleChangeRole} checked={user.role === 'ophthalmologist'} type='radio' value='ophthalmologist' />
            </Label>
            <Label>
              Paciente
              <Input name='role' onChange={handleChangeRole} checked={user.role === 'patient'} type='radio' value='patient' />
            </Label>
            <Label>
              Admin
              <Input name='role' onChange={handleChangeRole} checked={user.role === 'admin'} type='radio' value='admin' />
            </Label>
          </RoleWrapper>
          {user.role === 'ophthalmologist' && (
            <StatusWrapper>
              <Label>
                Nuevo estudio
                <Input name='status' onChange={handleChangeOphthalmologistStatus} checked={user.status === 'new'} type='radio' value='new' />
              </Label>
              <Label>
                Continuar estudio
                <Input name='status' onChange={handleChangeOphthalmologistStatus} checked={user.status === 'continue'} type='radio' value='continue' />
              </Label>
              <Label>
                Bloqueado
                <Input name='status' onChange={handleChangeOphthalmologistStatus} checked={user.status === 'locked'} type='radio' value='locked' />
              </Label>
              <Label>
                Desbloquear
                <Input name='status' onChange={handleChangeOphthalmologistStatus} checked={user.status === 'unlock'} type='radio' value='unlock' />
              </Label>

            </StatusWrapper>
          )}
        </UserMenu>
        {/* Cuando se quiera habilitar la funcion de desplegar menu desde usuario: quitar comentario linea de abajo */}
        {/* <AvatarLetter onClick={toggleShowUser}> */}
        <AvatarLetter>
          {user.role.charAt(0).toUpperCase()}
        </AvatarLetter>
      </AvatarSide>
    </Container>
  )
}

export default Header
